import {
  RECORD_NUMBER_COST_CENTER_SUFFIXES,
  RECORD_NUMBER_DIGITS,
  RECORD_NUMBER_EXAMPLES,
  RECORD_NUMBER_LIVESTOCK_SUFFIXES,
  RECORD_NUMBER_MODULE_PREFIXES,
  RECORD_NUMBER_RULES,
} from "./recordNumberConfig";

function normalizeKey(value) {
  return String(value || "").trim();
}

export function formatRecordSequence(sequence, digits = RECORD_NUMBER_DIGITS) {
  const numeric = Number(sequence || 0);
  if (!Number.isFinite(numeric) || numeric <= 0) return String(1).padStart(digits, "0");
  return String(Math.trunc(numeric)).padStart(digits, "0");
}

export function buildRecordNumber({ prefix, sequence, suffix }) {
  const cleanPrefix = normalizeKey(prefix).toUpperCase();
  const cleanSequence = formatRecordSequence(sequence);
  const cleanSuffix = normalizeKey(suffix).toUpperCase();
  if (!cleanPrefix) return cleanSuffix ? `${cleanSequence}-${cleanSuffix}` : cleanSequence;
  return cleanSuffix ? `${cleanPrefix}-${cleanSequence}-${cleanSuffix}` : `${cleanPrefix}-${cleanSequence}`;
}

export function parseRecordNumber(recordNumber) {
  const raw = normalizeKey(recordNumber).toUpperCase();
  if (!raw) return { prefix: "", sequence: "", suffix: "" };
  const parts = raw.split("-").filter(Boolean);
  if (parts.length >= 3) return { prefix: parts[0], sequence: parts[1], suffix: parts.slice(2).join("-") };
  if (parts.length === 2) return { prefix: parts[0], sequence: parts[1], suffix: "" };
  return { prefix: "", sequence: parts[0] || "", suffix: "" };
}

export function getRecordNumberRule(moduleKey) {
  return RECORD_NUMBER_RULES[normalizeKey(moduleKey)] || null;
}

export function getRecordPrefix(moduleKey) {
  const rule = getRecordNumberRule(moduleKey);
  return normalizeKey(rule?.prefix || RECORD_NUMBER_MODULE_PREFIXES[normalizeKey(moduleKey)] || "").toUpperCase();
}

export function getCostCenterSuffix(costCenter, overrides = {}) {
  const raw = normalizeKey(costCenter);
  if (!raw) return "";
  const merged = { ...RECORD_NUMBER_COST_CENTER_SUFFIXES, ...overrides };
  return normalizeKey(merged[raw] || merged[raw.toLowerCase()] || "").toUpperCase();
}

export function getLivestockSuffix(category, overrides = {}) {
  const raw = normalizeKey(category);
  if (!raw) return "";
  const merged = { ...RECORD_NUMBER_LIVESTOCK_SUFFIXES, ...overrides };
  return normalizeKey(merged[raw] || merged[raw.toLowerCase()] || "").toUpperCase();
}

export function buildModuleRecordNumber({ moduleKey, sequence, costCenter, livestockType, suffix, costCenterCodeMap, livestockCodeMap }) {
  const rule = getRecordNumberRule(moduleKey);
  const prefix = getRecordPrefix(moduleKey);
  let resolvedSuffix = normalizeKey(suffix).toUpperCase();
  if (!resolvedSuffix && rule?.suffixKind === "costCenter") {
    resolvedSuffix = getCostCenterSuffix(costCenter, costCenterCodeMap);
  }
  if (!resolvedSuffix && rule?.suffixKind === "livestock") {
    resolvedSuffix = getLivestockSuffix(livestockType, livestockCodeMap);
  }
  return buildRecordNumber({ prefix, sequence, suffix: resolvedSuffix });
}

export function extractHighestRecordSequence(records, prefix) {
  const cleanPrefix = normalizeKey(prefix).toUpperCase();
  return (records || []).reduce((maxValue, record) => {
    const direct = Number(record?.recordSequence || 0);
    if (Number.isFinite(direct) && direct > maxValue) return direct;
    const parsed = parseRecordNumber(record?.recordNumber || "");
    if (!parsed.prefix || parsed.prefix !== cleanPrefix) return maxValue;
    const parsedValue = Number(parsed.sequence || 0);
    return Number.isFinite(parsedValue) && parsedValue > maxValue ? parsedValue : maxValue;
  }, 0);
}

export function buildNextRecordNumber(records, options) {
  const prefix = getRecordPrefix(options?.moduleKey);
  const nextSequence = extractHighestRecordSequence(records, prefix) + 1;
  return {
    recordPrefix: prefix,
    recordSequence: nextSequence,
    recordSuffix: normalizeKey(options?.suffix).toUpperCase() || undefined,
    recordNumber: buildModuleRecordNumber({ ...options, sequence: nextSequence }),
  };
}

export function getRecordNumberExample(moduleKey) {
  return RECORD_NUMBER_EXAMPLES[normalizeKey(moduleKey)] || "";
}

export function mapCostCenterCodes(entries) {
  return Object.fromEntries((entries || []).map((entry) => {
    const name = normalizeKey(entry?.name);
    const code = normalizeKey(entry?.code).toUpperCase();
    return name ? [name, code] : null;
  }).filter(Boolean));
}
